import { ApolloQueryResult, FetchResult, gql } from '@apollo/client';
import { inject, injectable } from 'tsyringe';
import {
  WorkIntervalCreateResponse,
  CleaningPlanDeleteResponse,
  WorkIntervalListResponse,
  IWorkIntervalUtc,
  InCreation,
  WorkIntervalUpdateResponse,
} from './interfaces/CleaningPlan.types';
import { AmGraphQlClient } from 'app/cross-cutting-concerns/communication/clients/AmGraphQlClient';
import {
  MutationWorkIntervalDeleteArgs,
  QueryWorkIntervalsArgs,
  MutationWorkIntervalUpdateArgs,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';

export type CleaningPlanClientListWorkIntervalOptions = QueryWorkIntervalsArgs;
export type CleaningPlanClientCreateWorkIntervalOptions = InCreation<IWorkIntervalUtc>;
export type CleaningPlanClientDeleteWorkIntervalOptions = MutationWorkIntervalDeleteArgs;
export type CleaningPlanClientUpdateWorkIntervalOptions = MutationWorkIntervalUpdateArgs;

@injectable()
export class CleaningPlanClient {
  constructor(@inject('AmGraphQlClient') private client: AmGraphQlClient) {}

  public listWorkIntervals = async ({
    filter,
  }: CleaningPlanClientListWorkIntervalOptions): Promise<ApolloQueryResult<WorkIntervalListResponse>> =>
    this.client.query({
      query: gql`
        query CleaningPlans($filter: InputFilterWorkIntervalsList!) {
          workIntervals(filter: $filter) {
            data {
              weekdaysUtc
              startMinutesUtc
              startHoursUtc
              recurring
              machine {
                id
                name
                type {
                  name
                }
              }
              id
              durationMs
              createdAt
              plannedDurationMs
            }
          }
        }
      `,
      variables: {
        filter,
      },
      context: {
        skipApiCache: true,
      },
    });

  public createWorkInterval = async ({
    machineId,
    startHoursUtc,
    startMinutesUtc,
    durationMs,
    createdAt,
    weekdaysUtc,
    recurring,
    plannedDurationMs,
  }: CleaningPlanClientCreateWorkIntervalOptions): Promise<FetchResult<WorkIntervalCreateResponse>> =>
    this.client.mutate({
      mutation: gql`
        mutation WorkIntervalCreate($input: InputWorkIntervalCreate!) {
          workIntervalCreate(input: $input) {
            data {
              weekdaysUtc
              startMinutesUtc
              startHoursUtc
              recurring
              id
              durationMs
              customerId
              createdAt
              plannedDurationMs
            }
          }
        }
      `,
      variables: {
        input: {
          machineId,
          startHoursUtc,
          startMinutesUtc,
          durationMs,
          createdAt,
          weekdaysUtc,
          recurring,
          plannedDurationMs,
        },
      },
    });

  public deleteWorkInterval = async ({
    id,
  }: CleaningPlanClientDeleteWorkIntervalOptions): Promise<FetchResult<CleaningPlanDeleteResponse>> =>
    this.client.mutate({
      mutation: gql`
        mutation WorkIntervalDelete($id: ID!) {
          workIntervalDelete(id: $id) {
            data
          }
        }
      `,
      variables: {
        id,
      },
    });

  public updateWorkInterval = async ({
    id,
    input,
  }: CleaningPlanClientUpdateWorkIntervalOptions): Promise<FetchResult<WorkIntervalUpdateResponse>> =>
    this.client.mutate({
      mutation: gql`
        mutation WorkIntervalUpdate($id: ID!, $input: InputWorkIntervalUpdate!) {
          workIntervalUpdate(id: $id, input: $input) {
            data {
              id
              startHoursUtc
              startMinutesUtc
              durationMs
              createdAt
              weekdaysUtc
              recurring
              plannedDurationMs
            }
          }
        }
      `,
      variables: {
        id,
        input,
      },
    });
}
